import { classMap, customElement, html } from '@adobe/edex/ui/edex-lit';
import { isEmpty, once } from 'lodash-es';
import { AdobeAnalyticsEvent, EdexDomID, EdexTagName } from '@adobe/edex/ui/shared/constants';
import '@spectrum-web-components/link/sp-link';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-preview';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-bookmark-single-outline';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-share-light';
import '@adobe/edex/ui/components/shared/tags/tags';
import '@adobe/edex/ui/components/shared/rating/rating';
import '@adobe/edex/ui/components/shared/comments/comment-list';
import '@adobe/edex/ui/shared/components/resource-type/resource-type';
import '@adobe/edex/ui/components/shared/edex-html/edex-html';
import '@adobe/edex/ui/components/resource/related-resource-shelf/related-resource-shelf';
import '@adobe/edex/ui/components/resource/resource-sidebar/resource-sidebar';
import { ResourceBase } from '@adobe/edex/ui/components/resource/resource.base';
import { getResourceHelpfulInfo, triggerOnceIfVisible } from '@adobe/edex/ui/utils';
import { $t } from '@adobe/edex/ui/shared/config/i18n';
import { Analytics } from '@adobe/edex/ui/shared/config/analytics';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { AnalyticsEvent, CC_LICENSE_NONE_ID } from '@adobe/edex/ui/constants';
import { styleMap } from '@adobe/edex/ui/edex-lit';
import { getDomainOfURL } from '@adobe/edex/ui/shared/utils';
import { EdexSEO } from '@adobe/edex/ui/shared/types/ui';
import { getResourceDetailsUrl } from '@adobe/edex/ui/shared/utils/routes';
import { formatPreviewItems } from '../attachment-preview/utils';

import '@adobe/edex/ui/components/resource/attachment-preview/attachment-preview';

const css = () =>
  import(
    /* webpackChunkName: "component/resource-element-styles" */
    /* webpackPreload: true */
    /* webpackMode: "lazy" */
    '@adobe/edex/ui/components/resource/resource-details/resource-details.scss'
  );

@customElement(EdexTagName.resourceDetails)
export class ResourceDetails extends ResourceBase {
  siteSection = 'Teaching Resources';
  highlightCommentID = '';
  get useLightDom() {
    return true;
  }

  get seo(): EdexSEO {
    if (!this.resource || this.builderPreview) {
      return null;
    }
    const {
      seoProps: { title, description, image, noIndex },
    } = this;
    return {
      title,
      canonicalURL: `${document.location.origin}${getResourceDetailsUrl(this.resource.vanityURL)}`,
      meta: [
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: description,
        },
        {
          name: 'Description',
          content: description,
        },
        {
          property: 'og:image',
          content: image,
        },
        {
          property: 'og:image:width',
          content: '1200',
        },
        {
          property: 'og:image:height',
          content: '627',
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:url',
          content: document.location.href,
        },
        //Twitter Metatag
        {
          property: 'twitter:url',
          content: document.location.href,
        },
        {
          property: 'twitter:domain',
          content: getDomainOfURL(document.location.origin),
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:description',
          content: description,
        },
        {
          name: 'twitter:image',
          content: image,
        },
        {
          name: 'robots',
          content: noIndex ? 'noindex' : 'all',
        },
      ],
    };
  }

  analyticsViewEvent = AnalyticsEvent.resourceView;
  unsubscribe: (() => void)[];

  fetchRelatedResourcesOnce = once(() => {
    this.unsubscribe = triggerOnceIfVisible(
      [...this.querySelectorAll<HTMLElement>('edex-related-resource-shelf')],
      this.fetchRelatedResources
    );
  });

  async connectedCallback() {
    await css();
    super.connectedCallback();
    if (this.locationSearch?.includes('fromPersonalisation=true')) {
      Analytics.getInstance().emitEvent(AdobeAnalyticsEvent.personalisedResourceClick, 'personalised resource clicked');
    }
  }

  disconnectedCallback() {
    this.unsubscribe?.forEach((cb) => cb());
    super.disconnectedCallback();
  }

  async updated(changedProperties) {
    await super.updated(changedProperties);

    if (this.canFetchRelatedResources) {
      this.fetchRelatedResourcesOnce();
    }
    // we are redirecting to SEO optimized URL from vanity URL CCEDU-4180
    if (this.isReplaceStateRequired) {
      window.history.replaceState(
        {},
        `${this.resource.title}`.concat('| Adobe Education Exchange'),
        `${document.location.origin}${getResourceDetailsUrl(this.resource.SEOUrl)}`
      );
    }
    if (!isEmpty(this.highlightCommentID) && this.updateComplete) {
      this.querySelector('#commentsdiv').scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  renderRelatedResources = () => {
    const {
      resource: { relatedResources },
    } = this;
    return html`
      <edex-related-resource-shelf .relatedResources=${relatedResources}></edex-related-resource-shelf>
    `;
  };

  renderNoInfoVariant3(header: I18nKey) {
    return html`
      <div class="no-info-container">
        <h2 class="no-info-header--sm">${$t(header)}</h2>
        <em>${$t(I18nKey.resourceBuilderNoInfoEntered)}</em>
      </div>
    `;
  }

  renderHelpfulInfos = () => {
    const items = getResourceHelpfulInfo(this.resource);
    if (items.length === 0) {
      return null;
    }
    return html`
      <div>${this.renderHelpfulInfo(false, items)}</div>
    `;
  };

  getQueryParamValue(key: string) {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
  }

  renderLicense = () => {
    const {
      resource: { copyLicenses },
    } = this;
    const license = copyLicenses?.[0];

    if (!license) {
      return this.builderPreview ? this.renderNoInfoVariant3(I18nKey.ccLicense) : null;
    }

    if (license.id === CC_LICENSE_NONE_ID) {
      return null;
    }
    return html`
      <div class="resource-license-container-new" id="${EdexDomID.license}">
        <h2 class="resource-license-header">${$t(I18nKey.ccLicense)}</h2>
        <div class="resource-license">
          <p>${$t(license.i18nLabel)}</p>
          <a
            href="${license.legalCodeURL}"
            target="_blank"
            class="resource-license-icon"
            style="${styleMap({
              'background-image': `url('${license.iconURL}')`,
              display: license.iconURL ? 'block' : 'none',
            })}"
          >
            <span class="visually-hidden">Creative Commons license</span>
          </a>
        </div>
      </div>
    `;
  };

  renderHeader = () => {
    return html`
      <div class="resource-details-header">
        <div style="display: flex;">
          <div class="resource-header-title-container">${this.renderTitle()}</div>
          <div class="resource-header-toolbar" style="margin-left: auto">
            ${this.renderToolbar(EdexDomID.desktopToolbar)}
          </div>
        </div>
      </div>
    `;
  };

  renderSidebar = () => {
    return html`
      <div class="details-preview col-lg-5 col-xs-12 height">
        <edex-resource-sidebar .resource=${this.resource} />
      </div>
    `;
  };
  renderItemsPreview = () => {
    const { resource } = this;
    return html`
      <edex-attachment-preview
        class="col-lg-7 col-xs-12 height ${classMap({
          'fs-ccx-template-tag': this.hasExpressTemplate(resource),
        })}"
        .resourceID=${resource.id}
        .attachments=${formatPreviewItems(resource)}
      ></edex-attachment-preview>
    `;
  };

  renderPreviewAndSideBar = () => {
    return html`
      <div class="grid grid-twelve-column">${this.renderItemsPreview()} ${this.renderSidebar()}</div>
    `;
  };

  renderMain() {
    return html`
      <div class="resource-container-two padding">
        ${this.renderHeader()} ${this.renderPreviewAndSideBar()}
        <div class="extra-padding">
          ${this.renderRelatedResources()} ${this.renderHelpfulInfos()} ${this.renderLicense()}
        </div>
      </div>
    `;
  }
}
